/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: UserActivityGrid.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Data grid específico para User Activity con filtros integrados
────────────────────────────────────────────────────────────────*/

import { User } from 'lucide-react';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import DateRangePicker, { DateRange } from '../../../components/ui/DateRangePicker';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { UserActivityFilterValues } from '../types/userActivity.types';
import { ActivityActionMultiSelect } from './ActivityActionMultiSelect';

interface UserActivityGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
  loading: boolean;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: UserActivityFilterValues;
  onSearch: (query: string) => void;
  onSort: (column: string) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onColumnToggle: (key: string) => void;
  onFilterChange: (filters: UserActivityFilterValues) => void;
}

export function UserActivityGrid({
  data,
  columns,
  loading,
  pagination,
  searchQuery,
  sortBy,
  sortOrder,
  filters,
  onSearch,
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onColumnToggle,
  onFilterChange,
}: UserActivityGridProps) {
  const { t } = useI18n();

  // Build DateRange value from filters
  const dateRangeValue: DateRange = {
    startDate: filters.startDate ? filters.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
    endDate:   filters.endDate   ? filters.endDate.split('T')[0]   : new Date().toISOString().split('T')[0],
    startTime: filters.startDate && filters.startDate.includes('T') ? filters.startDate.split('T')[1].slice(0, 5) : '00:00',
    endTime:   filters.endDate   && filters.endDate.includes('T')   ? filters.endDate.split('T')[1].slice(0, 5)   : '23:59',
  };

  return (
    <div className="user-activity-wrapper">
      {/* Filters Card - PRIMERO */}
      <div className="pt-16">
        <div className="p-3 bg-card/75 backdrop-blur-md border border-border/50 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

            {/* Row 1: Date Range | Actions */}
            <Label className="text-left">
              {t('userActivity.filters.dateRange') || 'Date Range'}
            </Label>
            <DateRangePicker
              value={dateRangeValue}
              onChange={(range: DateRange) => onFilterChange({
                ...filters,
                startDate: `${range.startDate}T${range.startTime || '00:00'}`,
                endDate:   `${range.endDate}T${range.endTime   || '23:59'}`,
              })}
            />
            <SimpleTooltip
              content={t('userActivity.filters.action.tooltip')}
              title={t('userActivity.filters.action.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('userActivity.filters.action.label')}</Label>
            </SimpleTooltip>
            <ActivityActionMultiSelect
              selectedActions={filters.actions}
              onChange={(actions) => onFilterChange({ ...filters, actions })}
              placeholder={t('userActivity.filters.action.all')}
            />

            {/* Row 2: Resource (2 cols) */}
            <SimpleTooltip
              content={t('userActivity.filters.resource.tooltip')}
              title={t('userActivity.filters.resource.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('userActivity.filters.resource.label')}</Label>
            </SimpleTooltip>
            <Input
              type="text"
              value={filters.resource}
              onChange={(e) => onFilterChange({ ...filters, resource: e.target.value })}
              placeholder={t('userActivity.filters.resource.placeholder')}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Data Grid - DESPUÉS */}
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={onSearch}
        onSort={onSort}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
        onColumnToggle={onColumnToggle}
        title={t('userActivity.title')}
        titleIcon={<User className="h-5 w-5 text-primary" />}
        searchPlaceholder={t('userActivity.searchPlaceholder')}
        emptyMessage={t('userActivity.emptyMessage')}
      />
    </div>
  );
}

// Global CSS to remove spacing between filter box and grid
if (typeof document !== 'undefined') {
  const styleId = 'user-activity-spacing-fix';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .user-activity-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
      
      .user-activity-wrapper .space-y-6 > :not([hidden]) ~ :not([hidden]) {
        margin-top: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }
}
